import isArray from 'lodash/isArray';
import isFunction from 'lodash/isFunction';
import isObject from 'lodash/isObject';
import PropTypes from 'prop-types';
import React from 'react';
import Grid from '../grid';
import ModalModule from '../modal';
import UI from '../ui';
import Utils from '../utils';

const { Communication } = Utils;
const { GridPanel } = Grid;
const { Modal, ModalButtonActionType, ModalContext } = ModalModule;
const { Spinner, WrappedInputText } = UI;

const TagCellRenderer = ({ data = {}, node, selectedTags = [] }) => {
	const { Color = '', Tag = '', OrderTagID } = data;

	if (selectedTags.includes(OrderTagID)) {
		node.setSelected(true);
	}

	return (
		<div className="change-tags-modal-cell-renderer">
			<div
				className="color-bar"
				style={{
					background: Color,
				}}
			/>
			{Tag}
		</div>
	);
};

export default class ChangeTagsModal extends React.Component {
	constructor(props) {
		super(props);

		const {
			data: { selectedTags },
		} = props;

		this.state = {
			columnDefs: [
				{
					field: 'Tag',
					filter: true,
					flex: 1,
					checkboxSelection: true,
					cellRenderer: 'TagCellRenderer',
					cellRendererParams: {
						selectedTags,
					},
				},
			],
			frameworkComponents: {
				TagCellRenderer,
			},
			isLoading: true,
			selectedTagIds: [],
			tagOptions: [],
		};

		this.gridApi = null;
	}

	componentDidMount = () => {
		this.getTags();
	};

	/**
	 * call the endpoint to get the tag options
	 * @return {array}
	 */
	getTags = async () => {
		const { showError } = this.context;
		const response = await Communication.getTags();
		const tagOptions =
			isObject(response.data) && isArray(response.data.tags)
				? response.data.tags
				: [];

		if (!Communication.isSuccessful(response)) {
			return showError({ response });
		}

		this.setState({
			tagOptions,
			isLoading: false,
		});
	};

	/**
	 * pass the new selected tag ids back to the caller
	 * @param {func} closeModal
	 */
	onSaveTags = closeModal => {
		const { selectedTagIds } = this.state;
		const {
			data: { onChangeSelectedTagsCallback },
		} = this.props;

		if (isFunction(onChangeSelectedTagsCallback)) {
			onChangeSelectedTagsCallback(selectedTagIds);
		}

		closeModal();
	};

	onGridReady = ({ api }) => {
		this.gridApi = api;
	};

	/**
	 * search tags
	 * @param {object}
	 */
	onChangeSearch = ({ target: { value } }) => {
		if (this.gridApi) {
			this.gridApi.setQuickFilter(value);
		}
	};

	/**
	 * multiple tag selection changes triggered by the grid
	 * @param {array} selection the selected tag
	 */
	onSelectionChanged = selections => {
		this.setState({
			selectedTagIds: selections.map(({ data }) => data.OrderTagID),
		});
	};

	render() {
		const { modalId } = this.props;
		const { columnDefs, frameworkComponents, tagOptions, isLoading } =
			this.state;
		const primaryButton = {
			type: ModalButtonActionType.Ok,
			text: 'Save',
			onClick: this.onSaveTags,
			dataqa: 'change-tags-save-button',
		};

		return (
			<Modal
				modalId={modalId}
				className="common-tags change-tags-modal"
				header="Change Tags"
				primaryButton={primaryButton}
				size="small"
			>
				<div className="p-grid">
					<p className="p-col-12">
						Choose which tags the selected products should have:
					</p>
					<div className="p-col-12 search-tags">
						<WrappedInputText
							dataqa="search-tags"
							name="searchbar"
							placeholder="Search Tags"
							onChange={this.onChangeSearch}
						/>
						<i className="pi pi-search" aria-label="Search" />
					</div>
					<div className="p-col-12">
						<GridPanel
							columnDefs={columnDefs}
							dataqa="change-tags-grid"
							domLayout="autoHeight"
							frameworkComponents={frameworkComponents}
							headerHeight={0}
							rowData={tagOptions}
							rowMultiSelectWithClick
							showPagination={false}
							onGridReady={this.onGridReady}
							onSelectionChanged={this.onSelectionChanged}
						/>
					</div>
					{isLoading ? <Spinner msg="Updating Tags" /> : null}
				</div>
			</Modal>
		);
	}
}

ChangeTagsModal.contextType = ModalContext;

ChangeTagsModal.propTypes = {
	// an array of selected tag ids
	selectedTags: PropTypes.arrayOf(PropTypes.number),

	// callback to receive the new selected tag ids
	onChangeSelectedTagsCallback: PropTypes.func,
};
